import React from "react";
import { observer } from "mobx-react-lite";
import { Col, Form, Row } from "react-bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import type TransferRefundForRefundOfRefundsDetailViewModel from "viewModels/finance/refundForRefundOfRefunds/transferRefundForRefundOfRefunds/transferRefundForRefundOfRefundsDetailViewModel";
import { formatAmount, formatDate } from "utils/localizations";
import type CounterpartyBankAccountResponse from "entities/counterpartyBankAccountResponse";
import Section from "../../../common/section";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import type TransferType from "entities/transferType";

const TransferRefundForRefundOfRefundPaymentSectionView: ViewComponent<TransferRefundForRefundOfRefundsDetailViewModel> =
  observer(({ vm }) => {
    return (
      <Section title={vm.translate("transferPayment.title")} context={vm.item.originalPayment}>
        <Row>
          <Form.Group as={Col} xs={6} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "senderName")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="senderName" />
          </Form.Group>
          <Form.Group as={Col} xs={6} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("senderBankAccount", "accountNumber")}</Form.Label>
            <FakeInput
              target={vm.item.originalPayment}
              property="senderBankAccount"
              valueFormatter={(account?: CounterpartyBankAccountResponse) => formatBankAccount(account)}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} sm={12} md={6} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "variableSymbol")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="variableSymbol" />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "constantSymbol")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="constantSymbol" />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "specificSymbol")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="specificSymbol" />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={12} md={6} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "paymentType")}</Form.Label>
            <FakeInput
              target={vm.item.originalPayment}
              property="transferType"
              valueFormatter={(paymentType: TransferType) => vm.translateRefundType(paymentType)}
            />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "postingDate")}</Form.Label>
            <FakeInput
              target={vm.item.originalPayment}
              property="postingDate"
              valueFormatter={(date?: Date) => (date ? formatDate(new Date(date)) : undefined)}
            />
          </Form.Group>
          <Form.Group as={Col} xs={6} md={3} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "paidAmount")}</Form.Label>
            <FakeInput
              target={vm.item.originalPayment}
              property="paidAmount"
              valueFormatter={(amount: number) => (vm.item.originalPayment ? formatAmount(amount) : undefined)}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={12} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "messageForRecipient")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="messageForRecipient" />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={12} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "additionalInformation")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="additionalInformation" />
          </Form.Group>
        </Row>
        <hr />
        <Row>
          <Form.Group as={Col} xs={12} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "senderAddress")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="senderAddress" />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={6} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "bankTransactionId")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="bankTransactionId" />
          </Form.Group>
          <Form.Group as={Col} xs={6} className="mb-3">
            <Form.Label>{vm.translateModelAttribute("transferOriginalPayment", "externalBankTransactionId")}</Form.Label>
            <FakeInput target={vm.item.originalPayment} property="externalBankTransactionId" />
          </Form.Group>
        </Row>
      </Section>
    );
  });

export default TransferRefundForRefundOfRefundPaymentSectionView;
